import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {Accordion, Icon, Table} from 'semantic-ui-react';

import Chip from '@ui/Chip';
import Divider from '@ui/Divider';
import useUserTimezone from '@utils/hooks/useUserTimezone';
import {formatFromUtcTimezone, formatMicrosecondsToDateTime} from '@utils/date';


const PvHistory = ({ history, eventId }) => {
    const [pvIndex, setPvIndex] = useState(-1);
    const userTz = useUserTimezone();
    const changesKeys = Object.keys(history);
    const changesCount = changesKeys.length ?? 0;

    const renderTableHeader = () => (
        <Table.Header>
            <Table.Row>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Event / Program</Table.HeaderCell>
                <Table.HeaderCell>League / Tournament</Table.HeaderCell>
                <Table.HeaderCell>Images</Table.HeaderCell>
                <Table.HeaderCell>Timing</Table.HeaderCell>
                <Table.HeaderCell>Signal / Source / Tunnel</Table.HeaderCell>
            </Table.Row>
        </Table.Header>
    );

    const renderTableRows = (changesKeys, history, changesCount) => (
        changesKeys.map((key, i) => {
            const change = history[key];
            const isLatest = (0 === i);
            const isInitial = (i === (changesCount - 1));
            const isCancelled = (true === change.isCancelled);
            const hasSourceId = (null !== change.sourceId);
            const rowStyle = isLatest ? {} : { opacity: 0.5 };

            return (
                <Table.Row key={key} style={rowStyle} className='pv-history-row'>
                    <Table.Cell>
                        <Chip size='small' color='default' label={formatMicrosecondsToDateTime(key, userTz)} />
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                            {isLatest ? <Chip size='small' color='primary' label='Latest' /> : null}
                            {isInitial ? <Chip size='small' color='primary' label='Initial' /> : null}
                            {isCancelled ? (
                                <Chip size='small' color='error' label='Cancelled' />
                            ) : (
                                <Chip size='small' color='success' label='Scheduled' />
                            )}
                            {hasSourceId ? (
                                <Chip size='small' color='success' label='Has Source' />
                            ) : (
                                <Chip size='small' color='default' label='No Source' />
                            )}
                        </div>
                    </Table.Cell>
                    <Table.Cell>
                        {change.eventTitle && change.eventId ? (
                            <>
                                <div style={{fontWeight:'bold'}}>{change.eventTitle}</div>
                                <div style={{color:'#888', fontSize:'0.9em'}}>{change.eventId}</div>
                            </>
                        ) : (
                            <div style={{color:'red', fontWeight:'bold'}}>Invalid title pattern</div>
                        )}
                        <div style={{fontWeight:'bold', marginTop: 4}}>{change.programTitle}</div>
                        <div style={{color:'#888', fontSize:'0.9em'}}>{change.programId}</div>
                    </Table.Cell>
                    <Table.Cell>
                        <div style={{fontWeight:'bold', marginTop: 4}}>{change.leagueName}</div>
                        <div style={{fontWeight:'bold'}}>{change.tournamentName}</div>
                    </Table.Cell>
                    <Table.Cell>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                            {change.imgEvent && <div><b>Event Image:</b> <span style={{ wordBreak: 'break-all' }}>{change.imgEvent}</span></div>}
                            {change.imgBackground && <div><b>Background Image:</b> <span style={{ wordBreak: 'break-all' }}>{change.imgBackground}</span></div>}
                        </div>
                    </Table.Cell>
                    <Table.Cell>
                        <div><b>Start:</b> {formatFromUtcTimezone(change.startTimeInUtc, userTz)}</div>
                        <div><b>Length:</b> {change.runLength}</div>
                    </Table.Cell>
                    <Table.Cell>
                        <div><b>Signal:</b> {change.signalId}</div>
                        <div><b>Source:</b> {change.sourceId}</div>
                        <div><b>Tunnel:</b> {change.tunnelName}</div>
                    </Table.Cell>
                </Table.Row>
            );
        })
    );

    if (!history || 0 === changesCount) {
        return null;
    }

    const style = `
    .pv-history-row:hover {
      background: #f0f4fa !important;
      transition: background 0.2s;
    }
    `;

    return (
        <>
            <style>{style}</style>
            <Divider />
            <Accordion fluid styled inverted className='eventContent' id={`pv_event_history-${eventId}`}>
                <Accordion.Title
                    active={0 === pvIndex}
                    index={0}
                    onClick={(e, titleProps) => {
                        const { index } = titleProps;
                        const newIndex = index === pvIndex ? -1 : index;
                        setPvIndex(newIndex);
                    }}
                >
                    <Icon name='dropdown' />
                    PV History
                </Accordion.Title>
                <Accordion.Content active={0 === pvIndex}>
                    <Table compact size='small' basic='very' celled style={{ margin: '8px 0', width: '100%', tableLayout: 'fixed', wordBreak: 'break-word' }}>
                        {renderTableHeader()}
                        <Table.Body>
                            {renderTableRows(changesKeys, history, changesCount)}
                        </Table.Body>
                    </Table>
                </Accordion.Content>
            </Accordion>
        </>
    );
};

PvHistory.propTypes = {
    history: PropTypes.object.isRequired,
    eventId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default PvHistory;
